import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { formatAdTypeLabels } from '@/components/doohReport/DeliveryDrivers/deliveryDrivers.helpers';
import { getScreenFilterOptions, matchesScreenFilters, SCREEN_FILTER_KEYS } from './screenFilters';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { FilterableScreen, PickedScreenFilters, ScreenFilterKey } from './screenFilters';

type SelectorChange = SelectorOptionPropsInterface | SelectorOptionPropsInterface[];

export interface ScreenFilterConfig {
  label: string;
  onChange: (options: SelectorChange) => void;
  options: SelectorOptionPropsInterface[];
  value: SelectorOptionPropsInterface[];
}

const ALL_VALUE = '';

const formatLabel = (key: ScreenFilterKey, value: string): string =>
  key === 'creativeFormat' ? formatAdTypeLabels([value])[0] : value;

/** Filters of the screens list (every one defaults to "All") and the predicate that applies them. */
export function useScreensFilters(screens: FilterableScreen[]): {
  activeCount: number;
  clear: () => void;
  filters: ScreenFilterConfig[];
  hasActive: boolean;
  matches: (screen: FilterableScreen) => boolean;
} {
  const { formatMessage } = useIntl();
  const [picked, setPicked] = useState<PickedScreenFilters>({});

  const labels: Record<ScreenFilterKey, string> = {
    adExchange: formatMessage({ defaultMessage: 'AdExchange', id: 'multiFlightListsMap.filters.adExchange.label' }),
    creativeFormat: formatMessage({
      defaultMessage: 'Creative Format',
      id: 'multiFlightListsMap.filters.creativeFormat.label'
    }),
    publisher: formatMessage({ defaultMessage: 'Publisher', id: 'multiFlightListsMap.filters.publisher.label' }),
    resolution: formatMessage({
      defaultMessage: 'Screen Resolution',
      id: 'multiFlightListsMap.filters.resolution.label'
    }),
    venueType: formatMessage({ defaultMessage: 'Venue Type', id: 'multiFlightListsMap.filters.venueType.label' })
  };
  const allOption: SelectorOptionPropsInterface = {
    id: 'all',
    label: formatMessage({ defaultMessage: 'All', id: 'multiFlightListsMap.filters.all' }),
    value: ALL_VALUE
  };

  const available = useMemo(
    () =>
      Object.fromEntries(SCREEN_FILTER_KEYS.map(key => [key, getScreenFilterOptions(screens, key)])) as Record<
        ScreenFilterKey,
        string[]
      >,
    [screens]
  );
  // Picked values that disappeared from the list no longer filter anything
  const active = useMemo(
    () =>
      Object.fromEntries(
        SCREEN_FILTER_KEYS.map(key => [key, (picked[key] ?? []).filter(value => available[key].includes(value))])
      ) as PickedScreenFilters,
    [available, picked]
  );
  const matches = useCallback((screen: FilterableScreen) => matchesScreenFilters(screen, active), [active]);

  const toFilter = (key: ScreenFilterKey): ScreenFilterConfig => {
    const options = [
      allOption,
      ...available[key].map(value => ({ id: `${key}-${value}`, label: formatLabel(key, value), value }))
    ];
    const activeValues = active[key] ?? [];

    const onChange = (selected: SelectorChange) => {
      const options = Array.isArray(selected) ? selected : [selected];
      const values = options.filter(option => option.value !== ALL_VALUE).map(option => String(option.value));
      // Picking "All" while values are picked clears them; picking a value while "All" is shown replaces it
      const pickedAll = activeValues.length > 0 && options.some(option => option.value === ALL_VALUE);
      setPicked(previous => ({ ...previous, [key]: pickedAll ? [] : values }));
    };

    return {
      label: labels[key],
      onChange,
      options,
      value: activeValues.length ? options.filter(option => activeValues.includes(String(option.value))) : [allOption]
    };
  };

  const activeCount = SCREEN_FILTER_KEYS.filter(key => (active[key] ?? []).length > 0).length;

  return {
    activeCount,
    clear: () => setPicked({}),
    filters: SCREEN_FILTER_KEYS.map(toFilter),
    hasActive: activeCount > 0,
    matches
  };
}
